// @flow
import React, { PureComponent } from 'react';
import { StyleSheet, View } from 'react-native';
import type {
  NavigationRoute,
  NavigationScreenProp,
} from '@react-navigation/compat';
import TrpcLibrary from 'digicel-kaizen-library/lib/tripica-library';
import type { Region } from 'digicel-kaizen-library';
import find from 'lodash/find';

import { FullButton, GradientPage } from '../../../components/common';
import I18n from '../../../lib/i18n';
import { RegionItem } from './components';
import PageTitle from '../../../components/signUp/PageTitle.web';

type RegionConfig = {
  name: string,
  text: string,
  iconName: string,
};

type Props = {|
  navigation: NavigationScreenProp<NavigationRoute>,
  setRegion: (region: Region) => void,
  selectedRegion: ?Region,
  goToNextPage: () => void,
|};

const REGIONS_CONFIGS: Array<RegionConfig> = [
  { name: 'GUF', text: I18n.t('SignUp.ChooseRegion.regionName.GUF'), iconName: 'guyane' },
  { name: 'GLP', text: I18n.t('SignUp.ChooseRegion.regionName.GLP'), iconName: 'guadeloupe' },
  { name: 'MTQ', text: I18n.t('SignUp.ChooseRegion.regionName.MTQ'), iconName: 'martinique' },
  { name: 'MAF', text: I18n.t('SignUp.ChooseRegion.regionName.MAF'), iconName: 'saint-martin' },
  { name: 'BLM', text: I18n.t('SignUp.ChooseRegion.regionName.BLM'), iconName: 'saint-barthelemy' },
];

const REGIONS: Array<Region> = TrpcLibrary.getRegions();

const getResponsiveSizes = (width) => {
  const iconSize = Math.min(Math.max(width * 0.09, 60), 150);
  const fontSize = Math.min(Math.max(width * 0.014, 15), 20);
  return { iconSize: Math.round(iconSize), fontSize: Math.round(fontSize) };
};

class ChooseRegion extends PureComponent<Props> {
  state = {
    windowWidth: window.innerWidth,
  };

  componentDidMount() {
    window.addEventListener('resize', this.onResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    const width = window.innerWidth;
    if (width !== this.state.windowWidth) {
      this.setState({ windowWidth: width });
    }
  };

  takeRegion = (name: string): ?Region =>
    find(REGIONS, region => region.name === name);

  isRegionSelected = (region: Region): boolean =>
    !!this.props.selectedRegion && region.name === this.props.selectedRegion.name;

  renderRegions = () => {
    const { iconSize, fontSize } = getResponsiveSizes(this.state.windowWidth);

    return REGIONS_CONFIGS.map<RegionItem>((regionConfig: RegionConfig) => {
      const region = this.takeRegion(regionConfig.name);

      if (!region) return null;

      return (
        <RegionItem
          text={regionConfig.text}
          iconName={regionConfig.iconName}
          region={region}
          key={region.name}
          isSelected={this.isRegionSelected(region)}
          onPress={this.props.setRegion}
          testID={region.name}
          iconSize={iconSize}
          fontSize={fontSize}
        />
      );
    });
  };

  render() {
    return (
      <GradientPage>
        <View style={styles.pageContainer}>
          <PageTitle
            title={I18n.t('SignUp.ChooseRegion.title')}
          />
          <View style={styles.regionsContainer} testID={'chooseRegionRegions'}>
            {this.renderRegions()}
          </View>
          <FullButton
            onPress={this.props.goToNextPage}
            style={styles.button}
            text={I18n.t('SignUp.ChooseRegion.continueButton')}
            testID={'chooseRegionButton'}
            disabled={!this.props.selectedRegion}
          />
        </View>
      </GradientPage>
    );
  }
}

const styles = StyleSheet.create({
  button: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
    height: 50,
    marginVertical: 16,
  },
  pageContainer: {
    flex: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  regionsContainer: {
    justifyContent: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: 80,
    rowGap: 30,
    marginVertical: 50,
    width: '100%',
  },
});

export default ChooseRegion;
